/**
 * RacePicker Component
 * ====================
 * Display and select races from an event, grouped by date.
 */

import React from 'react';

function RacePicker({ event, selectedRace, onSelect, categoryProgress }) {
  if (!event || !event.races) {
    return <p>No races available for this event.</p>;
  }

  const races = event.races;

  // Determine local status for each race
  const getLocalStatus = (race) => {
    const key = race.code || race.fisCodex;
    const progress = categoryProgress[key];
    
    if (!progress) return { label: '⏳ Not Started', className: 'pending' };
    
    const hasFis = !!race.fisCodex;
    const fisExported = progress.fisExported;
    const ussaExported = progress.ussaExported;
    
    // Both FIS and USSA race - need both exports
    if (hasFis) {
      if (fisExported && ussaExported) return { label: '✅ FIS + USSA', className: 'complete' };
      if (fisExported) return { label: '🔄 FIS Done', className: 'in-progress' };
      if (ussaExported) return { label: '🔄 USSA Done', className: 'in-progress' };
    } else {
      // USSA-only race
      if (ussaExported) return { label: '✅ USSA Done', className: 'complete' };
    }
    
    return { label: '⏳ Not Started', className: 'pending' };
  };

  // Extract YYYY-MM-DD and format as natural text with day of week
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Get just the date part (YYYY-MM-DD) to avoid timezone issues
    const datePart = dateStr.slice(0, 10);
    const [year, month, day] = datePart.split('-');
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    const monthName = monthNames[date.getMonth()];
    const dayName = dayNames[date.getDay()];
    
    return `${monthName} ${parseInt(day, 10)}, ${year} (${dayName})`;
  };
  
  // Get just the date portion for grouping (YYYY-MM-DD)
  const getDateKey = (dateStr) => {
    if (!dateStr) return 'unknown';
    return dateStr.slice(0, 10);
  };

  const formatTechnique = (tech) => {
    if (!tech) return '';
    const trimmed = tech.trim().toUpperCase();
    if (trimmed === 'F' || trimmed === 'FR' || trimmed.startsWith('FR')) return 'Free';
    if (trimmed === 'C' || trimmed === 'CL' || trimmed.startsWith('CL')) return 'Classic';
    return trimmed;
  };

  const formatGender = (gender) => {
    if (!gender) return '';
    const g = gender.toLowerCase();
    if (g === 'male' || g === 'm') return 'Male';
    if (g === 'female' || g === 'f') return 'Female';
    return gender;
  };

  // Group races by date (using YYYY-MM-DD to avoid timezone issues)
  const groupedByDate = races.reduce((acc, race) => {
    const dateKey = getDateKey(race.date);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(race);
    return acc;
  }, {});

  // Sort dates chronologically
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => 
    new Date(a) - new Date(b)
  );

  // Build header text for a day's races
  const buildDayHeader = (dateStr, dayRaces) => {
    const dateDisplay = formatDate(dateStr);
    
    // Collect unique discipline+technique combinations
    const combos = new Set();
    dayRaces.forEach(race => {
      const discipline = race.discipline || '';
      const technique = formatTechnique(race.technique);
      if (discipline || technique) {
        combos.add(`${discipline} ${technique}`.trim());
      }
    });

    const comboStr = Array.from(combos).join(', ');
    return comboStr ? `${dateDisplay} — ${comboStr}` : dateDisplay;
  };

  // Get race type from various possible fields
  const getRaceType = (race) => {
    // Log race object once to see available fields
    if (races.indexOf(race) === 0) {
      console.log('[RacePicker] Sample race object:', race);
    }
    // Try various field locations for type
    return race.crossCountry?.type || race.type || race.eventType || '—';
  };

  return (
    <div className="race-picker">
      {sortedDates.map(dateKey => {
        const dayRaces = groupedByDate[dateKey];
        const headerText = buildDayHeader(dateKey, dayRaces);

        return (
          <div key={dateKey} className="race-day-group">
            <div className="race-day-header">{headerText}</div>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>FIS Codex</th>
                  <th>USSA Code</th>
                  <th>Type</th>
                  <th>Male/Female</th>
                  <th>Distance</th>
                  <th>Free/Classic</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dayRaces.map((race, index) => {
                  const isSelected = selectedRace?.code === race.code;
                  const status = getLocalStatus(race);
                  
                  return (
                    <tr
                      key={race.code || index}
                      className={`${isSelected ? 'selected' : ''} ${status.className}`}
                      onClick={() => onSelect(race)}
                    >
                      <td>
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => onSelect(race)}
                        />
                      </td>
                      <td>{race.fisCodex || '—'}</td>
                      <td>{race.code || '—'}</td>
                      <td>{getRaceType(race)}</td>
                      <td>{formatGender(race.gender)}</td>
                      <td>{race.crossCountry?.distance ? `${race.crossCountry.distance}km` : '—'}</td>
                      <td>{formatTechnique(race.technique)}</td>
                      <td><span className={`status ${status.className}`}>{status.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

export default RacePicker;

